// Relationship history (docs/specs/RELATIONSHIP_HISTORY.md; proposed steps 0.40-0.43, 10.14, 10.15): the words, types and
// small rules the screens share. Spoken to before (ReConnect) reads crm_spoken_before; "Bring in your history" (Your
// contacts) reads crm_history_status and writes through act (set_history_options, register_history_upload then
// add_history_upload, confirm_history_chat, import_history_rows); "Plan follow-ups" is act plan_follow_ups.

export type Warmth = "hot" | "warm" | "cool" | "cold";
export const WARMTHS: Warmth[] = ["hot", "warm", "cool", "cold"];
export const WARMTH_LABEL: Record<Warmth, string> = { hot: "Hot", warm: "Warm", cool: "Cool", cold: "Cold" };
export const WARMTH_HINT: Record<Warmth, string> = {
  hot: "Spoke in the last month, or keen",
  warm: "Friendly, in the last six months",
  cool: "Went quiet",
  cold: "Long ago, or it ended badly",
};

export type HistorySource = "hubspot" | "pipedrive" | "gmail" | "outlook" | "whatsapp" | "csv";
export const HISTORY_SOURCES: HistorySource[] = ["hubspot", "pipedrive", "gmail", "outlook", "whatsapp", "csv"];
export const SOURCE_LABEL: Record<string, string> = {
  hubspot: "HubSpot",
  pipedrive: "Pipedrive",
  gmail: "Gmail",
  outlook: "Outlook",
  whatsapp: "WhatsApp",
  csv: "Contact file",
  archive: "Earlier records",
  import: "Import",
};

export const MODULE_FILTERS = ["find", "attract", "network", "connect", "reconnect", "complete", "closed"] as const;

export interface SpokenRow {
  summary_id: string;
  person_id: string | null;
  full_name: string | null;
  role: string | null;
  has_email: boolean;
  organisation_id: string | null;
  organisation: string | null;
  warmth: Warmth;
  last_contact_at: string | null;
  first_contact_at: string | null;
  last_channel: string | null;
  last_direction: "in" | "out" | null;
  interactions_count: number;
  last_topic: string | null;
  how_it_ended: string | null;
  suggested_follow_up: string | null;
  open_promises: string[];
  sources: string[];
  written_by: "rules" | "claude";
  module: string | null;
  module_label: string | null;
  follow_up: { status: string; at: string; step: string } | null;
  /** 10.16 Their latest suggested or confirmed move (crm_spoken_before, migration 20261124090000). */
  move?: { id: string; status: "suggested" | "confirmed"; to_name: string | null; confidence: number } | null;
}

export interface SpokenFilters {
  warmth?: Warmth[];
  since?: string;
  until?: string;
  source?: string;
  module?: string;
  q?: string;
  /** 10.16 Only people who have moved job. */
  moved?: boolean;
}

/** The filters as crm_spoken_before takes them: empty values left out. */
export function filterParams(f: SpokenFilters): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (f.warmth?.length) out.warmth = f.warmth;
  if (f.since && /^\d{4}-\d{2}-\d{2}$/.test(f.since)) out.since = f.since;
  if (f.until && /^\d{4}-\d{2}-\d{2}$/.test(f.until)) out.until = f.until;
  if (f.source) out.source = f.source;
  if (f.module) out.module = f.module;
  if (f.q?.trim()) out.q = f.q.trim().slice(0, 100);
  if (f.moved) out.moved = "true";
  return out;
}

/** "Last contacted" presets: the since date for N months back from today. */
export function monthsAgo(months: number, now = new Date()): string {
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - months, now.getUTCDate()));
  return d.toISOString().slice(0, 10);
}

/** Whether a row can be ticked for "Plan follow-ups", and why not. */
export function tickable(r: SpokenRow): { ok: boolean; why?: string } {
  if (!r.person_id) return { ok: false, why: "No contact person: add one to their firm first" };
  if (r.follow_up && (r.follow_up.status === "open" || r.follow_up.status === "claimed")) return { ok: false, why: "A follow-up is already planned" };
  return { ok: true };
}

export interface PlanResult {
  queued: number;
  routed: Record<string, number>;
  skipped: Record<string, number>;
}

const ROUTED_WORDS: Record<string, string> = {
  connect: "in a conversation in Connect: a follow-up task is in your actions",
  network: "held by Network: a follow-up task is in your actions",
  complete: "customers: a follow-up task is in your actions",
  you: "with no firm on record: a follow-up task for you is in your actions",
};
const SKIPPED_WORDS: Record<string, string> = {
  suppressed: "asked not to be contacted",
  erased: "erased",
  no_history: "have no past conversations",
  already_planned: "already have a follow-up planned",
  not_found: "are no longer in your CRM",
  no_reconnect_stage: "could not be moved into ReConnect (no ReConnect stage)",
};

/** "2 follow-ups drafted for your approval; 1 in a conversation in Connect…; 1 left out: asked not to be contacted." */
export function planSummary(r: PlanResult): string {
  const parts: string[] = [];
  parts.push(
    r.queued
      ? `${r.queued} ${r.queued === 1 ? "follow-up" : "follow-ups"} being drafted. They will wait for your approval before anything is sent`
      : "No follow-ups drafted",
  );
  for (const [k, n] of Object.entries(r.routed ?? {})) if (n) parts.push(`${n} ${ROUTED_WORDS[k] ?? `held by ${k}`}`);
  const skipped = Object.entries(r.skipped ?? {}).filter(([, n]) => n > 0);
  if (skipped.length) parts.push(`left out: ${skipped.map(([k, n]) => `${n} ${SKIPPED_WORDS[k] ?? k.replace(/_/g, " ")}`).join(", ")}`);
  return parts.join("; ") + ".";
}

/** The timeline title of an imported item ("before_ogma:hubspot"), as "Before Ogma · HubSpot"; null otherwise. */
export function beforeOgmaLabel(title: string | null | undefined): string | null {
  if (!title || !title.startsWith("before_ogma:")) return null;
  const src = title.slice("before_ogma:".length);
  return `Before Ogma · ${SOURCE_LABEL[src] ?? src}`;
}

export interface HistoryOptions {
  months: number;
  keep_full_text: boolean;
  create_people: boolean;
  read_mailbox: boolean;
  crm: boolean;
  country?: string;
}

export interface HistoryUpload {
  id: string;
  file_name: string;
  status: "waiting" | "processing" | "done" | "failed";
  chats: number;
  imported: number;
  to_confirm: number;
  skipped: number;
  error: string | null;
  created_at: string;
  file_deleted: boolean;
}

export interface HistoryChat {
  id: string;
  title: string | null;
  contact_name: string | null;
  contact_phone: string | null;
  message_count: number;
  first_at: string | null;
  last_at: string | null;
}

export interface HistoryStatus {
  options: HistoryOptions;
  crm_provider: string | null;
  mailboxes: number;
  by_source: Record<string, number>;
  people: number;
  firms: number;
  last_imported_at: string | null;
  uploads: HistoryUpload[];
  chats_to_confirm: HistoryChat[];
}

export const DEFAULT_OPTIONS: HistoryOptions = { months: 24, keep_full_text: false, create_people: false, read_mailbox: false, crm: false };

/** The status read tolerantly: anything missing takes the database's default. */
export function readHistoryStatus(raw: unknown): HistoryStatus {
  const r = (raw && typeof raw === "object" ? raw : {}) as Partial<HistoryStatus>;
  return {
    options: { ...DEFAULT_OPTIONS, ...(r.options ?? {}) },
    crm_provider: r.crm_provider ?? null,
    mailboxes: Number(r.mailboxes ?? 0),
    by_source: r.by_source ?? {},
    people: Number(r.people ?? 0),
    firms: Number(r.firms ?? 0),
    last_imported_at: r.last_imported_at ?? null,
    uploads: Array.isArray(r.uploads) ? r.uploads : [],
    chats_to_confirm: Array.isArray(r.chats_to_confirm) ? r.chats_to_confirm : [],
  };
}

/** What the owner reads before uploading a WhatsApp chat. */
export const WHATSAPP_PRIVACY =
  "Ogma reads the chat once to note when you spoke and what about, keeps a one-line summary per day (never the messages themselves), and deletes the file straight away. Group chats are left out. Ogma never sends WhatsApp messages to these numbers: WhatsApp messages only ever go to people who have opted in.";

export const MAILBOX_PRIVACY =
  "Ogma reads your mail only with people already in your CRM or that you have emailed, keeps the date, the subject and a one-line preview of each message, and never the message itself. People who asked not to be contacted are left out.";

/** A file name safe for the upload path (letters, numbers, dots, dashes and underscores). */
export function safeFileName(name: string): string {
  const s = name.replace(/[^A-Za-z0-9._-]+/g, "-").replace(/^[-.]+|-+$/g, "").slice(0, 120);
  return s || "chat.txt";
}

export const MAX_UPLOAD_BYTES = 100 * 1024 * 1024;

/** A WhatsApp export the owner picked: a .txt or .zip under 100 MB. Null when fine, else why not. */
export function uploadProblem(file: { name: string; size: number }): string | null {
  if (!/\.(txt|zip)$/i.test(file.name)) return "Choose the .txt or .zip file WhatsApp made when you exported the chat.";
  if (file.size > MAX_UPLOAD_BYTES) return "That file is over 100 MB. Export the chat without media and try again.";
  if (file.size === 0) return "That file is empty.";
  return null;
}

// ---------------------------------------------------------------- the contact file's last contact and notes (0.28)
/** A date from a contact file cell as YYYY-MM-DD: 2026-03-01, 01/03/2026 or 1/3/26 (day first), or 1 March 2026. */
export function readDate(cell: string | undefined, now = new Date()): string | null {
  const s = (cell ?? "").trim();
  if (!s) return null;
  let y: number, m: number, d: number;
  let mm = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(s);
  if (mm) {
    [y, m, d] = [Number(mm[1]), Number(mm[2]), Number(mm[3])];
  } else if ((mm = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})$/.exec(s))) {
    [d, m, y] = [Number(mm[1]), Number(mm[2]), Number(mm[3])];
    if (y < 100) y += 2000;
  } else {
    const t = Date.parse(s);
    if (Number.isNaN(t)) return null;
    const dt = new Date(t);
    [y, m, d] = [dt.getFullYear(), dt.getMonth() + 1, dt.getDate()];
  }
  const out = new Date(Date.UTC(y, m - 1, d));
  if (out.getUTCMonth() !== m - 1 || out.getUTCDate() !== d) return null;
  if (out.getTime() > now.getTime() + 86_400_000) return null;
  return out.toISOString().slice(0, 10);
}

export interface HistoryRow {
  name?: string;
  email?: string;
  company?: string;
  domain?: string;
  last_contacted: string;
  notes?: string;
}
